import { isNative } from 'platform/native';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AccountBalanceTwoToneIcon from '@mui/icons-material/AccountBalanceTwoTone';
import BusinessTwoToneIcon from '@mui/icons-material/BusinessTwoTone';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import LogoutTwoToneIcon from '@mui/icons-material/LogoutTwoTone';
import PersonTwoToneIcon from '@mui/icons-material/PersonTwoTone';
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';

import { LocalizationContext } from 'contexts/LocalizationContext';
import { useSelector } from 'store';
import { NavLink } from 'react-router-dom';
import HeaderSettingsStatusIcon from '../HeaderSettingsStatusIcon';

const LIST_ITEM_ICON_SX = {
  minWidth: 40,
  color: 'primary.main',
  '& .MuiSvgIcon-root': { fontSize: 22 },
} as const;

const LIST_ITEM_BUTTON_SX = {
  borderRadius: '8px',
  py: 0.75,
} as const;

// ==============================|| PROFILE MENU ||============================== //

export default function ProfileSection() {
  const theme = useTheme();
  const { currentLocale } = useContext(LocalizationContext);
  const profileLoc = useMemo(() => currentLocale.settingsPopup.profileDropdown, [currentLocale]);

  const { logout } = useAuth();
  const user = useSelector((state) => state.user);
  const isAdmin = user?.Role === 'admin';
  const [open, setOpen] = useState(false);

  /**
   * anchorRef is used on different components and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef<any>(null);
  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (
    event: React.MouseEvent<HTMLDivElement | HTMLAnchorElement> | MouseEvent | TouchEvent,
  ) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const chipLabel = <HeaderSettingsStatusIcon suppressTooltip={open} />;

  return (
    <>
      <Chip
        sx={{
          ml: 2,
          height: '48px',
          alignItems: 'center',
          borderRadius: '27px',
          overflow: 'visible',
          position: 'relative',
          zIndex: 1,
          '& .MuiChip-label': {
            lineHeight: 0,
            overflow: 'visible',
            display: 'flex',
            alignItems: 'center',
            pr: 0.5,
            pl: 0.75,
            gap: 0.5,
            marginInlineStart: 0.5,
          },
        }}
        icon={
          <Avatar
            src={user?.Image}
            alt="user-images"
            sx={{
              ...theme.typography.mediumAvatar,
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer',
            }}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            color="inherit"
          />
        }
        label={chipLabel}
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
        aria-label="user-account"
      />
      <Popper
        placement="bottom"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{ zIndex: (t) => t.zIndex.tooltip + 25 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 14],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper dir={theme.direction}>
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        pt: 1.5,
                        pb: 1,
                        width: '100%',
                        maxWidth: 320,
                        minWidth: 260,
                        textAlign: 'start',
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {user?.FullName}
                      </Typography>
                      {user?.Email ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {user.Email}
                        </Typography>
                      ) : null}
                    </Box>
                    <Divider />
                    <Box
                      dir={theme.direction}
                      sx={{
                        px: 1.25,
                        py: 1,
                        height: '100%',
                        maxHeight: 'calc(100vh - 250px)',
                        overflowX: 'hidden',
                        '&::-webkit-scrollbar': { width: 5 },
                      }}
                    >
                      <List
                        component="nav"
                        disablePadding
                        sx={{
                          width: '100%',
                          '& .MuiListItemButton-root': { mt: 0.25 },
                        }}
                      >
                        {!isNative && (
                        <ListItemButton sx={LIST_ITEM_BUTTON_SX} component={NavLink} to="/company/details" onClick={handleClose}>
                          <ListItemIcon sx={LIST_ITEM_ICON_SX}>
                            <BusinessTwoToneIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="body2">{profileLoc.menuItems.company}</Typography>}
                          />
                        </ListItemButton>
                        )}

                        {!isNative && (
                        <ListItemButton sx={LIST_ITEM_BUTTON_SX} component={NavLink} to="/system-settings" onClick={handleClose}>
                          <ListItemIcon sx={LIST_ITEM_ICON_SX}>
                            <SettingsTwoToneIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="body2">{profileLoc.menuItems.systemSettings}</Typography>}
                          />
                        </ListItemButton>
                        )}

                        {isAdmin && !isNative ? (
                          <ListItemButton
                            sx={LIST_ITEM_BUTTON_SX}
                            component={NavLink}
                            to="/security-privacy/sessions"
                            onClick={handleClose}
                          >
                            <ListItemIcon sx={LIST_ITEM_ICON_SX}>
                              <ShieldOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2">
                                  {profileLoc.menuItems.securityPrivacy ?? 'אבטחה ופרטיות'}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        ) : null}

                        {!isNative && (
                        <ListItemButton sx={LIST_ITEM_BUTTON_SX} component={NavLink} to="/interface/manage" onClick={handleClose}>
                          <ListItemIcon sx={LIST_ITEM_ICON_SX}>
                            <DashboardCustomizeOutlinedIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="body2">{profileLoc.menuItems.interface}</Typography>}
                          />
                        </ListItemButton>
                        )}

                        <ListItemButton sx={LIST_ITEM_BUTTON_SX} component={NavLink} to="/customization" onClick={handleClose}>
                          <ListItemIcon sx={LIST_ITEM_ICON_SX}>
                            <PersonTwoToneIcon />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">{profileLoc.menuItems.profile}</Typography>} />
                        </ListItemButton>

                        {!isNative && (
                        <ListItemButton sx={LIST_ITEM_BUTTON_SX} component={NavLink} to="/account/about" onClick={handleClose}>
                          <ListItemIcon sx={LIST_ITEM_ICON_SX}>
                            <AccountBalanceTwoToneIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="body2">{profileLoc.menuItems.account}</Typography>}
                          />
                        </ListItemButton>
                        )}
                      </List>

                      <Divider
                        sx={{
                          my: 0.75,
                          borderColor: 'divider',
                          opacity: 0.65,
                        }}
                      />
                      <List component="nav" disablePadding>
                        <ListItemButton
                          sx={{
                            ...LIST_ITEM_BUTTON_SX,
                            color: 'error.main',
                            '& .MuiListItemIcon-root': { color: 'error.main' },
                          }}
                          onClick={handleLogout}
                        >
                          <ListItemIcon sx={{ ...LIST_ITEM_ICON_SX, color: 'error.main' }}>
                            <LogoutTwoToneIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500 }}>
                                {profileLoc.menuItems.logout}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
